import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import { DiagnosticOrderActionsService } from '../../services/diagnostic-order-actions.service';
import { IDiagnosticOrder, IRevision } from '../../interfaces/diagnostic-order.interface';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { TestActionsService } from '../../services/test-actions.service';
import { IWorkflowItem } from '@ispm/core/interfaces/workflow.interface';
import { ConfigurationStoreHandler } from '../../../../configuration/services/configuration-store-handler.service';
import { IStepConfiguration } from '../../../../configuration/interfaces/step-configuration.interface';
import { IConfigurationsMap } from '../../../../configuration/interfaces/definition.interface';
import { Observable } from 'rxjs/Observable';
import { FormHandlerService } from "@ispm/dynamic-form/services/form-handler.service";
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { set, get } from "lodash";
import { DiagnosticOrderService } from '../../services/diagnostic-order.service';
import { TestService } from '../../services/test.service';
import { Store } from "@ngrx/store";
import { currentOrderIsNewOrderSelector } from "@ispm/test/store/current-order.selector";
import { take, takeUntil } from 'rxjs/operators';
import { WorkflowService } from '@ispm/workflow/services/workflow.service';
import { AuthenticationService } from '@ispm/user/services/authentication.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { RouterManagerService } from "@ispm/core/services/router-manager.service";
import { ManageTagsService } from '@ispm/shared/services/manage-tags.service';
import { Subject } from 'rxjs/Subject';

@Component({
  selector: 'gen-test-info',
  templateUrl: './test-info.component.html',
  styleUrls: ['./test-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TestInfoComponent implements OnInit, OnDestroy {

  currentStepConfiguration$: Observable<IStepConfiguration<IConfigurationsMap>>;
  formsConfig: any[];
  QMSValues: any;
  isEditableField: boolean;
  isNewOrder:boolean = false;
  currentOrder: IDiagnosticOrder;
  currentRevision: IRevision;
  testConfiguration: any;
  inputs: any[];
  revisionId: string;
  testId: string;
  userId: string;
  reInitializeForm = false;
  isAssignee: boolean;
  formsOverrides: any = { };
  updateFormObject: any

  private activeJob: IWorkflowItem;
  private readonly afterInitialResultsJobs = ['initialResults', 'postTrials', 'TherapyMatch', 'interpretationResults', 'none', 'UiManualAberrationCuration', 'Final review'];
  private destroyer$: Subject<void> = new Subject<void>();

  constructor(
    private diagnosticOrderActionsService: DiagnosticOrderActionsService,
    private testService: TestService,
    private diagnosticOrderService: DiagnosticOrderService,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private authService: AuthenticationService,
    private workflowService: WorkflowService,
    private coreActionCreator: CoreActionCreatorService,
    private _store: Store<any>,
    private testActionsService: TestActionsService,
    configurationStoreHandler: ConfigurationStoreHandler,
    private routerManagerService: RouterManagerService,
    private manageTagsService: ManageTagsService,
    private _changeDetector: ChangeDetectorRef,
    private _ngZone: NgZone,
  ) {
    this.currentStepConfiguration$ = configurationStoreHandler.getConfigurationForCurrentStep(); // not present in old js
  }

  ngOnInit() {
    this.revisionId = this.routerManagerService.parameters.revisionId;
    this.testId = this.routerManagerService.parameters.testId;
    this.userId = this.authService.user.info.id;
    this._store.select(currentOrderIsNewOrderSelector).pipe(take(1)).subscribe(isNewOrder => this.isNewOrder = isNewOrder);
    const testConfiguration = this.testService.testConfiguration;

    this.manageTagsService.isTagsChanged
      .pipe(takeUntil(this.destroyer$.asObservable()))
      .subscribe((isTagsChanged: boolean) => {
        if (isTagsChanged) {
          this.reInitializeForm = isTagsChanged;
          this._changeDetector.markForCheck();
          this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
              this.reInitializeForm = false;
              this._changeDetector.markForCheck();
            }, 0);
          });
       }
      });

    if (testConfiguration) {
      this.QMSValues = testConfiguration.QMSValues;
      this.formsConfig = Array.isArray(testConfiguration.formsConfig) ? testConfiguration.formsConfig : [];
      this.testConfiguration = testConfiguration;
    }

    if (!this.formsConfig.some(item => item.key === '')) {
      const dynamicBasicForm = { key: '', title: '', formId: 'BASIC-TEST-DATA-DEFAULT'};
      this.formsConfig.unshift(dynamicBasicForm);
    }

    this.diagnosticOrderActionsService.getState()
      .filter(state => !!state.order && Object.keys(state.order).length > 0)
      .take(1)
      .map(state => state.order)
      .subscribe(current => {
        this.currentOrder = current;
        this.workflowService.currentTest = this.currentOrder;
        this.currentRevision = this.currentOrder.workflowRequest.revisions.find(rev => rev.id === this.revisionId);
        this.activeJob = this.currentRevision.items.find(item => item.name === this.currentOrder.workflowRequest.activeJobName);

        if (this.activeJob) {
          this.inputs = this.activeJob['inputs'];
        }

        const statusIsAfterInitialResults = !!this.activeJob && this.afterInitialResultsJobs.some( job => job.toLowerCase() === this.activeJob.name.toLowerCase());

        this.isAssignee = this.currentOrder.performer == this.userId;
        this.currentStepConfiguration$.take(1).subscribe((currentStepConfiguration) => {
          let editable: boolean;
          this.formsOverrides = FormHandlerService.BuildOverrides(currentStepConfiguration);
          if (currentStepConfiguration && currentStepConfiguration.fieldsValidation) {
            editable = currentStepConfiguration.fieldsValidation.isEditable;
          }
          else {
            editable = !statusIsAfterInitialResults && !testConfiguration.uneditableFields;
          }

          this.isEditableField = editable && this.isAssignee;
        });
        this.diagnosticOrderService.formalizeForView(this.currentOrder, null, false);
        this.coreActionCreator.setLoader(false);
      });
  }

  ngOnDestroy() {
    this.destroyer$.next();
    this.destroyer$.complete();
  }

  trackByFormId(formConfig: any): string {
    return formConfig.formId;
  }

  updateDiagnosticOrder(updatedProperties: string[], subForm: any, key: string = null): void {
    this.debouncedUpdateTestInfo('diagnosticOrder', updatedProperties, subForm, key);

  }

  debounce = (cb, minInterval) => {
    let timeout = undefined;
    return (...params) => {
      (timeout) && clearTimeout(timeout);
      timeout = setTimeout(() => cb(...params), minInterval)
    };
  }

  debouncedUpdateTestInfo = this.debounce((updateResourceName, updatedProperties: string[], subForm: any, key: string = null) => {
    if (key) {
      this.currentOrder[key] = subForm;
    } else {
      Object.assign(this.currentOrder, subForm);
    }
    if (updatedProperties && updatedProperties.length > 0){
      this.updateFormObject = {
        name: updatedProperties[updatedProperties.length - 1],
        value: subForm[updatedProperties[updatedProperties.length - 1]]
      };
    }

    this.updateTestInfo(updateResourceName, key ? updatedProperties.map(propertyName => `${key}.${propertyName}`) : updatedProperties);
  },1000)

  isInputExist(inputType: string, inputNames: string[]): boolean | any {
    return this.inputs ? this.inputs.find(input => {
      return inputNames ? inputNames.some(name => name === input.name) : input.type === inputType;
    }) : false;
  }

  inputFocusout(propertyName: string[], updateResourceName: string, value: any): void {
    if (value !== undefined) {
        this.updateTestInfo(updateResourceName, propertyName);
    }
  }

  isActiveStep = (stepName: string): boolean => {
    stepName = stepName.toLowerCase();
    let isActiveStep = false;
    const isJobActive = this.activeJob && this.activeJob.name.toLowerCase() === stepName;
    const isTestStatusNotDone = this.currentOrder.workflowRequest.status.toLowerCase() != 'done';
    const isTestNotCompleted = this.currentRevision.status.toLowerCase() !== 'test completed';
    switch (stepName) {
      case 'reviewtestresults':
      case '2ndopinionrequested':
      case '2ndopinionreview':
      case 'initialresults':
      case 'interpretationresults':
        isActiveStep = isJobActive && isTestNotCompleted && isTestStatusNotDone;
        break;
      case 'uigeneralinput':
      case 'reviewqcresults':
      case 'pipelinefailed':
        isActiveStep = this.activeJob && this.activeJob.name.toLowerCase().includes(stepName) && isTestNotCompleted && isTestStatusNotDone;
        break;
      case 'final review':
        isActiveStep = isJobActive && isTestNotCompleted && isTestStatusNotDone;
        break;
      case 'testcompleted':
        isActiveStep = !isTestNotCompleted;
        break;
    }
    return isActiveStep;
  }

  emitUpdate($event: { specimen: any, primaryTumor: any}) {
    this.currentOrder.specimen = $event.specimen;
    this.currentOrder['primaryTumor'] = $event.primaryTumor;

    let updatedFields = this.formalizeForServer($event);
      if (updatedFields.length > 0) {
        this.updateTestInfo('diagnosticOrder', updatedFields);
      }

    this.diagnosticOrderActionsService.setDiagnosticOrder(this.currentOrder);

    this.coreActionCreator.setLoader(false);
  }

  formalizeForServer(data: any) {
    this.currentOrder = Object.assign({}, this.currentOrder);
    this.workflowService.currentTest = this.currentOrder;
    data = Object.assign({}, data);
    let updatedFields = [];

    if(this.testConfiguration && this.testConfiguration.orderFieldsMapping) {
      this.testConfiguration.orderFieldsMapping.forEach((mapping) => {
        set(this.currentOrder, mapping.target, get(data, mapping.source));
        if (mapping.target.indexOf('specimen[') < 0) // in case the target is under specimen resource which is updated as well
          updatedFields.push(mapping.target);
      });
    }

    return updatedFields;
  }

  public performWorkflowStep(requiredActiveSteps: string[], stepToPerform: string, inputButtonArgs?: any, successMsg?: string): void {
    if (requiredActiveSteps.some(this.isActiveStep)) {
      this.coreActionCreator.setLoader(true);
      this.workflowService.performWorkflowStep(this.currentOrder, stepToPerform, null, this.revisionId, null, null, inputButtonArgs,false)
        .then(() => {
          if(successMsg){
            this.notificationMessageService.success(this.notificationMessage[successMsg]);
          }else{
            this.notificationMessageService.success(this.notificationMessage.SUCCESSFULLY_MOVED_TO_NEXT_STEP);
          }
            this.coreActionCreator.setLoader(false);
          this.routerManagerService.reload();
        }) .catch(() => {
          this.coreActionCreator.setLoader(false);
          this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_MOVE_TO_THE_NEXT_STEP);
        });
    }
  }

  confirmSpecimen(): void {
    this.performWorkflowStep(['reviewQCResults'], 'SpecimenProceed', undefined, 'SUCCESSFULLY_MOVED_TO_NEXT_STEP');
  }

  failTest(): void {
    this.performWorkflowStep(['pipelineFailed'], 'failTest', undefined, 'TEST_SUCCESSFULLY_ABORTED');
  }

  rejectSpecimen(): void {
    this.performWorkflowStep(['reviewQCResults', 'pipelineFailed'], 'SpecimenReject', undefined, 'SUCCESSFULLY_MOVED_BACK_TO_WETLAB');
  }

  private updateTestInfo(updateResourceName: string, updatedField: string[]): void {
    this.testActionsService.updateTestInfo(this.diagnosticOrderService.formalizeForServer(this.currentOrder), this.revisionId, updateResourceName, updatedField)
      .subscribe(newTest => {
        if (!newTest) { return; }
        this.currentOrder = newTest;
        this.workflowService.currentTest = this.currentOrder;
        this.diagnosticOrderActionsService.setDiagnosticOrder(newTest);
        // Timeout here is so that the loader will not hide untill the store diagnostic order is not updated
        setTimeout(()=>{
          Promise.resolve(null);
        }, 2000);
      });
  }

  public showDueTestType() {
     return !this.testService.isNavicanControlSample();
  }

  public isAdmin(){
    return this.authService.isAdminUser(this.authService.user);
  }
}
